import React from "react";

import { setScheduledTask } from "../../../store/api/tasks/reducer";
import { useStoreDispatch } from "../../../store/hooks";
import { PasswordField } from "../../Shared/Radix/Inputs/PasswordField";
import { isIPv4, isIPv6, isServer, isValidHostName, isValidServerPort } from "../../../utils/validations";
import { useSelector } from "react-redux";
import { tasksSelector } from "../../../store/api/tasks/selector";
import { setErrorMsg } from "../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { RadixSelectControl } from "../../ui/radix-select";
import { Label } from "../../ui/label";
import { InputField } from "../../Shared/Radix/Inputs/InputField";

interface IProps {
    taskFor?: string,
}
export const SoftwareDownloadAndUpdate: React.FC<IProps> = ({ taskFor }) => {
    const dispatch = useStoreDispatch();
    const errorMsg = useSelector(errorMsgSelector);
    const scheduledtasks = useSelector(tasksSelector);

    const onInputChange = (name: string, value: string) => {
        if (name === "fileName") {
            const fileArray = !!value ? value.split("/") : [];
            const localFileName = fileArray.length > 0 ? fileArray[fileArray.length - 1] : value;
            dispatch(setScheduledTask({ [name]: value, localFileName }));
        } else {
            dispatch(setScheduledTask({ [name]: value }));
        }
        dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === "password") {
            if (!value) dispatch(setErrorMsg({ [name]: "Password is required!" }));
        }
        if (name === "hostName") {
            if (!!value) {
                const isValidHost = isValidHostName(value) || isIPv4(value) || isIPv6(value) || isServer(value);
                if (!isValidHost) {
                    dispatch(setErrorMsg({ [name]: "Host Name is invalid!" }));
                }
            } else {
                dispatch(setErrorMsg({ [name]: "Host Name is required!" }));
            }
        }
        if (name === "port") {
            if (!!value) {
                const isValidHost = isValidServerPort(value);
                if (!isValidHost) {
                    dispatch(setErrorMsg({ [name]: "Port is invalid!" }));
                }
            }
        }
        if (name === "fileName") {
            if (!value) {
                dispatch(setErrorMsg({ [name]: "File Name is required!" }));
            }
        }
        if (name === "username") {
            if (!value) {
                dispatch(setErrorMsg({ [name]: "User Name is required!" }));
            }
        }
        if (name === "sshKeyIndex") {
            if (!value) {
                dispatch(setErrorMsg({ [name]: "SSH Key index is required!" }));
            }
        }
    };

    const selectChangeHandler = (name: string) => (value: string) => {
        if (name === "sshOperation") {
            if (value === "useSSHKey") {
                dispatch(setScheduledTask({ [name]: true, password: "" }));
                dispatch(setErrorMsg({ ...errorMsg, [name]: "", password: "" }));
            } else {
                dispatch(setScheduledTask({ [name]: false, sshKeyIndex: "" }));
                dispatch(setErrorMsg({ ...errorMsg, [name]: "", sshKeyIndex: "" }));
            }
        } else {
            dispatch(setScheduledTask({ [name]: value }));
            dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
        }
    };

    const onPasswordChange = (name: string, value: string) => {
        dispatch(setScheduledTask({ [name]: value }));
        dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    return (
        <div className="flex flex-col gap-6">
            <div className="space-y-2">
                <Label>Protocol</Label>
                <RadixSelectControl
                    options={[{ value: "scp", label: "scp" }]}
                    value={scheduledtasks?.protocol}
                    placeholder="__placeholder__"
                    onValueChange={selectChangeHandler("protocol")}
                    disabled={true}
                />
            </div>
            <div className="space-y-2">
                <Label>Server Type</Label>
                <RadixSelectControl
                    options={[
                        { value: "Global", label: "Global" },
                        { value: "Task Specific", label: "Task Specific" },
                    ]}
                    value={scheduledtasks?.serverType}
                    placeholder="__placeholder__"
                    onValueChange={selectChangeHandler("serverType")}
                />
            </div>
            <div>
                <InputField
                    label="Host Name"
                    name="hostName"
                    value={scheduledtasks?.hostName ?? ""}
                    required
                    onChange={onInputChange}
                    onBlur={onBlurHandler}
                    placeholder="Enter host name"
                />
            </div>
            <div>
                <InputField
                    label="Port"
                    name="port"
                    value={scheduledtasks?.port ?? ""}
                    type="number"
                    onChange={onInputChange}
                    onBlur={onBlurHandler}
                    placeholder="Enter port"
                />
            </div>
            <div>
                <InputField
                    label="File Name"
                    name="fileName"
                    value={scheduledtasks?.fileName ?? ""}
                    required
                    placeholder="Add file name with path (e.g: home/software/filename)"
                    onChange={onInputChange}
                    onBlur={onBlurHandler}/>
            </div>
            <div className="flex items-end justify-start gap-4">
                {[
                    { value: "useCredentials", label: "Use Credentials" },
                    { value: "useSSHKey", label: "Use SSH Key" },
                ].map((opt) => (
                    <label key={opt.value} className="flex items-center gap-1.5 cursor-pointer text-sm">
                        <input
                            type="radio"
                            name="sshOperation"
                            value={opt.value}
                            checked={scheduledtasks?.sshOperation ? opt.value === "useSSHKey" : opt.value === "useCredentials"}
                            onChange={() => selectChangeHandler("sshOperation")(opt.value)}
                            style={{ accentColor: "var(--primary)" }}
                        />
                        {opt.label}
                    </label>
                ))}
            </div>
            <div>
                <InputField
                    label="SSH Key Index"
                    name="sshKeyIndex"
                    value={scheduledtasks?.sshKeyIndex ?? ""}
                    disabled={!scheduledtasks?.sshOperation}
                    required
                    onChange={onInputChange}
                    onBlur={onBlurHandler}
                    placeholder="Enter ssh key index"
                />
            </div>
            <div>
                <InputField
                    label="Username"
                    name="username"
                    value={scheduledtasks?.username ?? ""}
                    required
                    onChange={onInputChange}
                    onBlur={onBlurHandler}
                    placeholder="Enter username"
                />
            </div>
            <div>
                <PasswordField
                    label="Password"
                    name="password"
                    value={scheduledtasks?.password ?? ""}
                    onChange={onPasswordChange}
                    onBlur={onBlurHandler}
                    disabled={scheduledtasks?.sshOperation}
                    placeholder="Enter password"
                />
            </div>
            {taskFor !== "softwareDownload" && (
                <div className="space-y-2">
                    <Label>Software Update Clean</Label>
                    <RadixSelectControl
                        options={[
                            { value: "None", label: "None" },
                            { value: "Db", label: "Db" },
                            { value: "Data", label: "Data" },
                            { value: "Configuration", label: "Configuration" },
                        ]}
                        value={scheduledtasks?.softwareUpdateClean || "None"}
                        placeholder="__placeholder__"
                        onValueChange={selectChangeHandler("softwareUpdateClean")}
                    />
                </div>
            )}
            <div>
                <p className="text-sm font-medium">{`${!!scheduledtasks?.sshKeyIndex ? "KeyIndex " + scheduledtasks?.sshKeyIndex : ""}  ${scheduledtasks?.protocol ?? ""}@${scheduledtasks?.hostName ?? ""}:${!!scheduledtasks?.port ? scheduledtasks?.port : 22}:/${scheduledtasks?.fileName ?? ""} .`}</p>
            </div>
        </div>
    );
};
